import React, { useState, useEffect } from 'react';
import { Search, Download, FileText, Filter, Calendar, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { formatDate, formatDateTime } from '../../../lib/dateUtils';
import { formatStatus, getStatusBadgeColor, getScoreColor } from '../../../lib/calculations';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import { db } from '../../../lib/db';
import { generateAppraisalExcel, generateAppraisalPDF, generateAppraisalListPDF } from '../../../lib/export';
import { toast } from 'sonner';

const COLORS = ['#0d9488', '#f59e0b', '#8b5cf6', '#10b981', '#ef4444'];

export default function ReportsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [appraisals, setAppraisals] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [appraisalsData, usersData] = await Promise.all([
          db.appraisals.toArray(),
          db.users.toArray()
        ]);
        setAppraisals(appraisalsData);
        setUsers(usersData.filter(u => u.role !== 'admin'));
      } catch (error) {
        console.error('Failed to load reports data', error);
        toast.error('Unable to load reports data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const getUserById = (id) => users.find(u => u.id === id);

  // Filter appraisals
  const filteredAppraisals = appraisals.filter(appraisal => {
    const user = getUserById(appraisal.userId);
    const matchesSearch = searchQuery === '' || 
      user?.profile?.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user?.profile?.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      appraisal.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || appraisal.status === statusFilter;
    const matchesDepartment = departmentFilter === 'all' || user?.profile?.department === departmentFilter;
    return matchesSearch && matchesStatus && matchesDepartment;
  });

  // Stats
  const statusStats = [
    { name: 'Finalized', value: appraisals.filter(a => a.status === 'finalized').length },
    { name: 'Pending Review', value: appraisals.filter(a => a.status === 'employee_completed').length },
    { name: 'In Progress', value: appraisals.filter(a => a.status === 'ready_to_evaluate').length },
  ];

  const scoreDistribution = [
    { range: '0-1', count: appraisals.filter(a => a.scores?.finalWeightedScore && a.scores.finalWeightedScore <= 1).length },
    { range: '1-2', count: appraisals.filter(a => a.scores?.finalWeightedScore && a.scores.finalWeightedScore > 1 && a.scores.finalWeightedScore <= 2).length },
    { range: '2-3', count: appraisals.filter(a => a.scores?.finalWeightedScore && a.scores.finalWeightedScore > 2 && a.scores.finalWeightedScore <= 3).length },
    { range: '3-4', count: appraisals.filter(a => a.scores?.finalWeightedScore && a.scores.finalWeightedScore > 3 && a.scores.finalWeightedScore <= 4).length },
    { range: '4-5', count: appraisals.filter(a => a.scores?.finalWeightedScore && a.scores.finalWeightedScore > 4).length },
  ];

  const departments = [...new Set(users.map(u => u.profile?.department).filter(Boolean))];

  const finalizedCount = appraisals.filter(a => a.status === 'finalized').length;
  const completionRate = appraisals.length ? Math.round((finalizedCount / appraisals.length) * 100) : 0;
  const scoredAppraisals = appraisals.filter(a => a.scores?.finalWeightedScore);
  const avgScore = scoredAppraisals.length
    ? (scoredAppraisals.reduce((sum, a) => sum + a.scores.finalWeightedScore, 0) / scoredAppraisals.length).toFixed(1)
    : '0.0';

  const handleExportPDF = async (appraisalId) => {
    const appraisal = appraisals.find(a => a.id === appraisalId);
    if (!appraisal) {
      toast.error('Appraisal not found');
      return;
    }
    const user = getUserById(appraisal.userId);
    if (!user) {
      toast.error('Employee not found');
      return;
    }
    try {
      const fileName = generateAppraisalPDF(appraisal, user);
      toast.success(`PDF exported: ${fileName}`);
    } catch (error) {
      console.error('Failed to export PDF', error);
      toast.error('Failed to export PDF');
    }
  };

  const handleExportExcel = () => {
    if (filteredAppraisals.length === 0) {
      toast.error('No appraisals to export');
      return;
    }
    try {
      const fileName = generateAppraisalExcel(filteredAppraisals, users);
      toast.success(`Excel exported (${filteredAppraisals.length}): ${fileName}`);
    } catch (error) {
      console.error('Failed to export Excel', error);
      toast.error('Failed to export Excel');
    }
  };

  const handleExportFilteredPDF = () => {
    if (filteredAppraisals.length === 0) {
      toast.error('No appraisals to export');
      return;
    }
    try {
      const fileName = generateAppraisalListPDF(filteredAppraisals, users);
      toast.success(`PDF exported (${filteredAppraisals.length}): ${fileName}`);
    } catch (error) {
      console.error('Failed to export PDF', error);
      toast.error('Failed to export PDF');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><span className="text-gray-500">Loading reports...</span></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports & Analytics</h1>
          <p className="text-gray-500 mt-1">View and export performance data</p>
        </div>
       
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Appraisals</p>
                <p className="text-3xl font-bold text-gray-900">{appraisals.length}</p>
              </div>
              <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-teal-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Completion Rate</p>
                <p className="text-3xl font-bold text-gray-900">{completionRate}%</p>
              </div>
              <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Avg Score</p>
                <p className="text-3xl font-bold text-gray-900">{avgScore}</p>
              </div>
              <div className="w-12 h-12 bg-violet-50 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-violet-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle>Status Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusStats}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {statusStats.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle>Score Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={scoreDistribution}>
                  <XAxis dataKey="range" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#0d9488" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters & Search */}
      <Card className="border-0 shadow-sm">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by employee name or appraisal title..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="finalized">Finalized</SelectItem>
                <SelectItem value="employee_completed">Pending Review</SelectItem>
                <SelectItem value="ready_to_evaluate">In Progress</SelectItem>
              </SelectContent>
            </Select>
            <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Department" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map(dept => (
                  <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Results Table */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <CardTitle>Appraisal Reports</CardTitle>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button onClick={handleExportExcel} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Download className="w-4 h-4 mr-1" /> Export Excel
            </Button>
            <Button variant="outline" onClick={handleExportFilteredPDF}>
              <Download className="w-4 h-4 mr-1" /> Export PDF
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Employee</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Appraisal</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Self Score</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Final Score</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Due Date</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Employee Submitted</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Manager Submitted</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAppraisals.map(appraisal => {
                  const user = getUserById(appraisal.userId);
                  return (
                    <tr key={appraisal.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                            {user?.profile?.firstName?.[0]}{user?.profile?.lastName?.[0]}
                          </div>
                          <div>
                            <p className="font-medium text-sm text-gray-900">
                              {user?.profile?.firstName} {user?.profile?.lastName}
                            </p>
                            <p className="text-xs text-gray-500">{user?.profile?.department}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">{appraisal.title}</td>
                      <td className="py-3 px-4">
                        <Badge className={getStatusBadgeColor(appraisal.status)}>
                          {formatStatus(appraisal.status)}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`font-medium ${getScoreColor(appraisal.scores?.employeeSelfTotal)}`}>
                          {appraisal.scores?.employeeSelfTotal?.toFixed(1) || '-'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`font-medium ${getScoreColor(appraisal.scores?.finalWeightedScore)}`}>
                          {appraisal.scores?.finalWeightedScore?.toFixed(1) || '-'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">{formatDate(appraisal.dueDate)}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{formatDateTime(appraisal.employeeSubmittedAt)}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{formatDateTime(appraisal.managerSubmittedAt || appraisal.finalizedAt)}</td>
                      <td className="py-3 px-4 text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleExportPDF(appraisal.id)}
                          disabled={appraisal.status !== 'finalized'}
                        >
                          <Download className="w-4 h-4 mr-1" /> PDF
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filteredAppraisals.length === 0 && (
              <div className="text-center py-6 text-gray-500">No appraisals match the current filters</div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
